import ms from "ms";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { Modal, modalService } from "@whatslly/ui-components/modals/ModalService";
import { getJSON } from "@whatslly/commons/apiUtils";
import type { CreateSidecar, GetSidecarStatus } from "@whatslly/types/sidecars-server/Sidecar";
import { ClientSideSidecar, SIDECAR_STATUS } from "@whatslly/types/sidecars-server/Sidecar";
import { ChannelPairingModal } from "./CommandModals";
import type { PairingModalProps } from "./ISidecarPairingStore";
import { fromPromise } from "mobx-utils";
import { TuvisNotificationsService } from "@whatslly/ui-components/services/TuvisNotificationsStore";
import { getPhoneCountryCode } from "@whatslly/commons/utils";
import type { SidecarInitPairingMode } from "@whatslly/types/sidecars-server/SidecarUrlParams";
import type { ChangeModeToState, PairingIdentification } from "./types";
import type { ISidecarPairingStore } from "./ISidecarPairingStore";
const PAIRING_REFRESH_INTERVAL = import.meta.env.MODE === "test" ? ms("100ms") : ms("2s");
@injectable()
export class SidecarPairingStore implements ISidecarPairingStore {
  pairingSidecar?: GetSidecarStatus;
  isPaired = false;
  phoneCountryCode = "+";
  pairingPhone = "";
  changeModeToState?: ChangeModeToState;
  initialPairingMode: SidecarInitPairingMode = "QR";
  initializePromise:
    | Promise<{
        success: boolean;
        pairing?: boolean;
        modal?: Modal<PairingModalProps>;
      }>
    | undefined;
  constructor(@inject(TuvisNotificationsService) private tuvisNotificationsService: TuvisNotificationsService) {
    makeObservable<SidecarPairingStore, "restartParingWithToken" | "restartParingWithOrg" | "setSidecarPhone" | "trackPairingSidecar">(this, {
      pairingSidecar: observable,
      isPaired: observable,
      changeModeToState: observable,
      pairingPhone: observable,
      phoneCountryCode: observable,
      setSidecar: action,
      changeMode: action,
      setPairingPhone: action,
      setCountryCode: action,
      changeModeRequest: computed,
      restartParingWithToken: action.bound,
      restartParingWithOrg: action.bound,
      setSidecarPhone: action,
      trackPairingSidecar: action,
      initialPairingMode: observable,
    });
  }

  static identify(identification: PairingIdentification) {
    const etokenSpread = identification.etoken ? { etoken: identification.etoken } : {};
    const searchParams = new URLSearchParams();
    if (identification.token) searchParams.set("token", identification.token);
    if (identification.orgId) searchParams.set("orgId", identification.orgId);
    return {
      searchParams: searchParams,
      searchParamsString: `${searchParams.size ? "?" : ""}${searchParams.toString()}`,
      etokenSpread,
    };
  }

  private async checkSidecarStatus(sidecarId: string, identification: PairingIdentification) {
    const { searchParamsString, etokenSpread } = SidecarPairingStore.identify(identification);
    return await getJSON({
      urlSuffix: `/sidecars/${sidecarId}${searchParamsString}`,
      pod: null as never,
      ...etokenSpread,
    }).then((res) => res.json() as Promise<GetSidecarStatus>);
  }

  async _initialize(sidecarId: string, identification: PairingIdentification, initPairingMode: SidecarInitPairingMode) {
    try {
      runInAction(() => {
        this.initialPairingMode = initPairingMode;
      });
      const status = await this.checkSidecarStatus(sidecarId, identification);
      if (!status.success) return { success: false };
      this.setSidecarPhone(status.sidecar);
      const lastUpdate = Date.now() - new Date(status.sidecar.updatedAt).getTime();
      if (status.sidecar.metadata?.isPaired) {
        this.setSidecar(status);
      }
      if ((status.sidecar.status === SIDECAR_STATUS.BOOTING || status.sidecar.status === SIDECAR_STATUS.PAIRING) && lastUpdate <= ms("30s")) {
        const modal = this.openModal(
          sidecarId,
          identification.token || identification.etoken || "",
          (modal) =>
            identification.etoken
              ? this.restartParingWithOrg(
                  modal,
                  sidecarId,
                  identification.etoken,
                  identification.orgId ?? "",
                  status.sidecar.runnerChannel ?? { type: "whatsapp" }
                )
              : this.restartParingWithToken(
                  modal,
                  sidecarId,
                  identification.token ?? "",
                  initPairingMode,
                  status.sidecar.runnerChannel?.type ?? "whatsapp"
                ),
          status.sidecar.runnerChannel?.type ?? "whatsapp"
        );
        this.trackPairingSidecar(sidecarId, identification, modal);
        this.setSidecar(status);
        if (status.sidecar.status === SIDECAR_STATUS.PAIRING) this.changeMode(status.sidecar.state.mode);
        return { success: true, pairing: true, modal };
      }

      return { success: true, pairing: false };
    } catch {
      return { success: false, pairing: false };
    }
  }

  async initialize(sidecarId: string, identification: PairingIdentification, initPairingMode: SidecarInitPairingMode) {
    if (this.initializePromise) return this.initializePromise;
    this.initializePromise = this._initialize(sidecarId, identification, initPairingMode);
    return this.initializePromise;
  }

  private restartParingWithToken(
    modal: Modal<any>,
    sidecarId: string,
    pairingToken: string,
    initPairingMode: SidecarInitPairingMode,
    runnerChannel: Exclude<CreateSidecar["channelType"], undefined>
  ) {
    modal?.close(false);
    this.pairingSidecar = undefined;
    this.changeModeToState = undefined;
    this.pairSidecar(sidecarId, pairingToken, initPairingMode, runnerChannel);
  }

  private restartParingWithOrg(
    modal: Modal<any>,
    sidecarId: string,
    etoken: string,
    orgId: string,
    runnerChannel: ClientSideSidecar["runnerChannel"]
  ) {
    modal?.close(false);
    this.pairingSidecar = undefined;
    this.changeModeToState = undefined;
    this.pairExistingSidecar({ _id: sidecarId, orgId, runnerChannel } as ClientSideSidecar, etoken);
  }

  setSidecar(status: GetSidecarStatus) {
    this.pairingSidecar = status;
    if (
      status.success &&
      (status.sidecar?.status === SIDECAR_STATUS.IDLE || status.sidecar?.status === SIDECAR_STATUS.RUNNING || status.sidecar?.metadata?.isPaired)
    ) {
      this.pairingSidecar = undefined;
      this.isPaired = true;
      return true;
    }
    return false;
  }

  private openModal(
    sidecarId: string,
    pairingToken: string,
    restartPairing: (modal: Modal<any>) => void,
    runnerChannel: Exclude<CreateSidecar["channelType"], undefined>,
    cancelable = true
  ) {
    const modal = modalService.openModal(ChannelPairingModal, {
      restartPairing: () => {
        restartPairing(modal);
      },
      onClose: () => {
        this.changeModeToState = undefined;
      },
      sidecarId,
      store: this,
      cancelable,
      runnerChannel,
    });
    return modal;
  }

  private async startSidecarPairing(sidecarId: string, identification: PairingIdentification, initPairingMode: SidecarInitPairingMode) {
    const { searchParams, etokenSpread } = SidecarPairingStore.identify(identification);
    searchParams.append("initPairingMode", initPairingMode);
    searchParams.append("resetStorage", "true");
    return await getJSON({
      urlSuffix: `/sidecars/${sidecarId}/start?${searchParams.toString()}`,
      pod: null as never,
      ...etokenSpread,
    }).then((res) => res.json() as Promise<GetSidecarStatus>);
  }

  async pairSidecar(
    sidecarId: string,
    pairingToken: string,
    initPairingMode: SidecarInitPairingMode,
    runnerChannel: Exclude<CreateSidecar["channelType"], undefined>
  ) {
    const modal = this.openModal(
      sidecarId,
      pairingToken,
      (modal) => this.restartParingWithToken(modal, sidecarId, pairingToken, initPairingMode, runnerChannel),
      runnerChannel
    );
    await this.startSidecarPairing(sidecarId, { token: pairingToken }, initPairingMode);
    await this.trackPairingSidecar(sidecarId, { token: pairingToken }, modal);
  }

  async pairExistingSidecar(sidecar: ClientSideSidecar, etoken: string): Promise<false | undefined> {
    const initPairingMode = "QR";
    const result = await this.initialize(sidecar._id, { etoken, orgId: sidecar.orgId }, initPairingMode);
    if (result.pairing) return result.modal?.promise;
    const modal = this.openModal(
      sidecar._id,
      etoken,
      (modal) => this.restartParingWithOrg(modal, sidecar._id, etoken, sidecar.orgId, sidecar.runnerChannel),
      sidecar.runnerChannel?.type ?? "whatsapp"
    );
    await this.startSidecarPairing(sidecar._id, { etoken, orgId: sidecar.orgId }, initPairingMode);
    await this.trackPairingSidecar(sidecar._id, { etoken, orgId: sidecar.orgId }, modal);
    await modal.promise;
  }
  private async trackPairingSidecar(sidecarId: string, identification: PairingIdentification, modal: Modal<any>) {
    this.pairingSidecar = undefined;
    const interval = setInterval(async () => {
      const data = await this.checkSidecarStatus(sidecarId, identification);
      const isPaired = this.setSidecar(data);

      if (data.success && data.sidecar.status === SIDECAR_STATUS.PAIRING) {
        this.tuvisNotificationsService.init(data.sidecar.uid).catch(() => {});

        if (!this.changeModeToState) {
          this.changeMode(data.sidecar.state.mode);
        }
      }
      if (isPaired) {
        modal?.close(true);
        this.changeModeToState = undefined;
      }
      await modal?.promise;
      clearInterval(interval);
    }, PAIRING_REFRESH_INTERVAL);
  }

  async changeMode(toState: ChangeModeToState) {
    this.changeModeToState = toState;
  }

  get changeModeRequest() {
    if (this.changeModeToState === "setPhoneNumber") return fromPromise(Promise.resolve<ChangeModeToState>("setPhoneNumber"));
    return fromPromise(
      new Promise<ChangeModeToState>((resolve) => {
        const interval = setInterval(() => {
          if (
            this.pairingSidecar?.success &&
            this.pairingSidecar.sidecar.status === SIDECAR_STATUS.PAIRING &&
            this.pairingSidecar.sidecar.state.mode === this.changeModeToState &&
            Date.now() - new Date(this.pairingSidecar.sidecar.updatedAt).getTime() <= ms("30s")
          ) {
            clearInterval(interval);
            resolve(this.changeModeToState);
          }
        }, 1000);
      })
    );
  }

  setCountryCode(countryCode: string) {
    this.phoneCountryCode = countryCode;
  }

  setPairingPhone(phone: string) {
    this.pairingPhone = phone;
  }

  private setSidecarPhone(sidecar: ClientSideSidecar) {
    if (sidecar.metadata?.phone && !this.pairingPhone) {
      const phone = sidecar.metadata.phone;
      const countryCode = getPhoneCountryCode(phone) ?? "";
      const phoneWithoutCodePrefix = phone.substring(countryCode.length, phone.length);
      this.setPairingPhone(phoneWithoutCodePrefix);
      countryCode && this.setCountryCode(countryCode);
    }
  }
}
